import './styles/EventModal.scss';

import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';
import { Attachment, Event, OrganizerType } from '@thro-types/events';

import { Formik } from 'formik';
import { omit } from 'lodash';
import omitDeep from 'omit-deep-lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import CREATE_MUTATION from 'gql/mutations/EventService/createEvent.gql';
import UPDATE_MUTATION from 'gql/mutations/EventService/updateEvent.gql';

import { Modal, ErrorBoundary, MultistepForm, ConfirmationDialog } from 'app/components/';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { actions as FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { uploadAttachmentToS3 } from '../helper';
import EventCreation from './components/EventCreation';
import { EventGroupOrganization } from './components/EventGroupOrganization';
import { EventOrganization } from './components/EventOrganization';
import EventPreview from './components/EventPreview';
import { eventValidationSchema, useEventInitialValues } from './validation';

interface Props {
  onCancel: (event?: any, reason?: any) => void;
  event: Event | undefined;
  closeHandler: () => void;
  createModalOpen: boolean;
  disabledCancel: boolean;
  setDisabledCancel?: Function;
}

const EventStep = ({ step, event }: { step: number; event?: Event }) => {
  switch (step) {
    case 0:
      return <EventOrganization />;
    case 1:
      return <EventCreation />;
    case 2:
      return <EventGroupOrganization previousEvent={event} />;
    case 3:
      return <EventPreview previousEvent={event} />;
    default:
      return null;
  }
};

export function EventModal(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const { user: currentUser } = useAuthContext();

  const steps = [
    t(translations.pages.events.create.steps.organization()),
    t(translations.pages.events.create.steps.informations()),
    t(translations.pages.events.create.steps.groups()),
    t(translations.pages.events.create.steps.preview())
  ];

  const [createEvent] = useMutation(CREATE_MUTATION, {
    context: { clientName: 'federation' }
  });

  const [updateEvent] = useMutation(UPDATE_MUTATION, {
    context: { clientName: 'federation' }
  });

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }

    if (values.attachments.length > 0) {
      const valuesToUpload: File[] = [];
      const alreadyUploadedAttachments: Attachment[] = [];
      let uploadResult: { name: string; path: string }[] = [];
      try {
        for (const attachment of values.attachments) {
          if (!attachment.id) {
            valuesToUpload.push(attachment);
          } else {
            alreadyUploadedAttachments.push(attachment);
          }
        }
        if (!!valuesToUpload.length) {
          const response = await uploadAttachmentToS3(valuesToUpload);
          uploadResult = response.map(res => ({ ...res, name: decodeURIComponent(res.name) }));
        }
        values.attachments = [...alreadyUploadedAttachments, ...uploadResult];
      } catch (e) {
        dispatch(AlertActions.error(t(translations.pages.events.alerts.failedAttachmentUpload())));
        setSubmitting(false);
        if (props.setDisabledCancel) {
          props.setDisabledCancel(false);
        }
        return;
      }
    }

    //! This must be removed in the future once each group has its own date configuration
    const groupsWithStartANDEndDate = values.groups.map(g => ({
      ...omit(
        g,
        '__typename',
        'enrollments',
        'createdAt',
        'updatedAt',
        'waitingList',
        'preCalcEnrollments'
      ),
      startDate: values.startDate,
      endDate: values.endDate
    }));
    //!
    if (values.id) {
      const saveBody = {
        ...omitDeep(
          values,
          '__typename',
          'enrollments',
          'createdAt',
          'updatedAt',
          'waitingList',
          'preCalcEnrollments'
        )
      };
      try {
        const omitFields = ['id', 'description'];
        await updateEvent({
          variables: {
            id: values.id,
            data: {
              ...omit(saveBody, omitFields),
              description: values.description,
              groups: groupsWithStartANDEndDate
            }
          },
          refetchQueries: ['GetEvents', 'GetEvent']
        });
        dispatch(AlertActions.success(t(translations.pages.events.alerts.successfulUpdate())));
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.events.alerts.failedUpdate())));
      }
    } else {
      try {
        const {
          data: { createEvent: newEvent }
        } = await createEvent({
          variables: {
            data: {
              ...values,
              description: values.description,
              isCancelled: false,
              groups: groupsWithStartANDEndDate,
              organizers: [
                ...values.organizers,
                { organizerType: OrganizerType.USER, refId: currentUser.id }
              ]
            }
          },
          refetchQueries: ['GetEvents']
        });
        dispatch(AlertActions.success(t(translations.pages.events.alerts.successfulCreate())));

        navigate(`/events/${newEvent.id}`);
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.events.alerts.failedCreate())));
      }
    }
    setSubmitting(false);
    props.closeHandler();
  };

  return (
    <ErrorBoundary boundaryTag="eventBoundary">
      <Modal
        open={props.createModalOpen}
        onClose={(_, reason) => {
          if (reason && reason === 'backdropClick') return;
          setCancelModalOpen(true);
        }}
        headline={
          props.event?.id
            ? t(translations.pages.events.create.editTitle())
            : t(translations.pages.events.create.title())
        }
        onCancel={() => {
          setCancelModalOpen(true);
        }}
        disabledCancel={props.disabledCancel}
      >
        <Grid className="event-modal">
          <Formik
            validateOnMount={!!props.event}
            initialValues={useEventInitialValues(props.event)}
            validationSchema={eventValidationSchema(t, props.event)}
            onSubmit={values => {
              setSubmitting(true);
              handleSaveClick(eventValidationSchema(t).cast(values));
            }}
          >
            <MultistepForm
              submitting={submitting}
              steps={steps}
              onCancel={props.onCancel}
              isStepperClickable={true}
            >
              {step => (
                <Grid container>
                  <EventStep step={step} event={props.event} />
                </Grid>
              )}
            </MultistepForm>
          </Formik>
        </Grid>
      </Modal>
      <ConfirmationDialog
        open={cancelModalOpen}
        title={t(translations.common.cancelConfirm.title())}
        onClose={() => {
          setCancelModalOpen(false);
        }}
        onConfirm={() => {
          props.onCancel();
          dispatch(FormErrorActions.close());
          setCancelModalOpen(false);
        }}
      >
        <div>{t(translations.common.cancelConfirm.message())}</div>
      </ConfirmationDialog>
    </ErrorBoundary>
  );
}
